import { useEffect, useState } from 'react';
import { _notNil } from '@/littledash';
import type {
  DataTableCellIdCoordinate,
  DataTableCellIndexCoordinate,
  DataTableCellSelectionChangeEvent,
} from '../DataTable.model';
import { DataTableCellSelectionState, DataTableEvent } from '@/components/UI/DataTableV2/DataTable.model';
import { useDataTable } from '../DataTableProvider';

export interface UseCellSelectionStateReturn {
  selectionState: DataTableCellSelectionState;
  isOrigin: boolean;
  isHighlighted: boolean;
}

/**
 * Track cell selection state (origin/highlighted/not-selected)
 *
 * This hook determines if a cell is the origin of a selection (the clicked cell),
 * highlighted (part of a selection range), or not selected. Uses AbortController for cleanup.
 *
 * @param coordinate - Cell index coordinate
 * @param idCoordinate - Cell ID coordinate (API IDs)
 * @returns Object containing selection state and helper booleans
 */
export const useCellSelectionState = (
  coordinate: DataTableCellIndexCoordinate,
  idCoordinate: DataTableCellIdCoordinate | undefined
): UseCellSelectionStateReturn => {
  const { dataTableService } = useDataTable();
  const [selectionState, setSelectionState] = useState<DataTableCellSelectionState>(() => {
    if (dataTableService.selection?.contains(coordinate) ?? false) {
      return dataTableService.selection?.from.column === coordinate.column &&
        dataTableService.selection?.from.row === coordinate.row
        ? DataTableCellSelectionState.OriginSelected
        : DataTableCellSelectionState.Highlighted;
    }
    return DataTableCellSelectionState.NotSelected;
  });

  useEffect(() => {
    if (_notNil(idCoordinate)) {
      const abortController = new AbortController();

      // Initialize selection state
      if (dataTableService.selection?.contains(coordinate) ?? false) {
        setSelectionState(
          dataTableService.selection?.from.column === coordinate.column &&
            dataTableService.selection?.from.row === coordinate.row
            ? DataTableCellSelectionState.OriginSelected
            : DataTableCellSelectionState.Highlighted
        );
      } else {
        setSelectionState(DataTableCellSelectionState.NotSelected);
      }

      const cellSelectionChangeListener = (event: DataTableCellSelectionChangeEvent) => {
        const { from } = event.detail.selection;
        if (event.detail.selection?.contains(coordinate)) {
          setSelectionState(
            from.column === coordinate.column && from.row === coordinate.row
              ? DataTableCellSelectionState.OriginSelected
              : DataTableCellSelectionState.Highlighted
          );
        } else {
          setSelectionState(DataTableCellSelectionState.NotSelected);
        }
      };

      dataTableService.subscribe(
        DataTableEvent.CellSelectionChange,
        cellSelectionChangeListener,
        abortController.signal
      );

      return () => {
        abortController.abort();
      };
    }
  }, [dataTableService, coordinate.row, coordinate.column, idCoordinate]);

  return {
    selectionState,
    isOrigin: selectionState === DataTableCellSelectionState.OriginSelected,
    isHighlighted: selectionState === DataTableCellSelectionState.Highlighted,
  };
};
